/* eslint-disable no-console */

// import util from 'util';
import path from "path";

import ReactDOMServer from "react-dom/server";
import ReactDOM from "react-dom";
import React from "react";
import { MDXProvider } from "@mdx-js/react";

import BaseMDX from "./pages/utils/BaseMDX";
import BaseContext from "./pages/utils/BaseContext";
import "./components";
import config from "./project.config";

const isServer = global.__lighterIsServer__; // eslint-disable-line no-underscore-dangle

if (isServer) {
  global.requestAnimationFrame = (cb) =>
    window.requestAnimationFrame || setTimeout(cb, 0);
}

const getContext = () =>
  require.context("../src", true, /\/pages\/[^/]*\.(js|mdx)$/);

function getPages({ css, js }) {
  const context = getContext();
  const replacePageExtension = (page, extension) =>
    `${path.basename(page, extension)}.html`;

  let rawPages = [];
  const contextKeys = context.keys();
  if (contextKeys.length) {
    rawPages = contextKeys.sort();
  }

  let pages = {};
  try {
    pages = Object.assign(
      ...rawPages.map((page) => {
        const baseContextValue = {
          css,
          js,
          pageId: page,
          isServer,
          pages: rawPages.map((pageName) =>
            replacePageExtension(pageName, path.extname(pageName))
          ),
          scope: config.scope,
          styleguidePath: config.styleguidePath,
          metaTheme: config.metaTheme,
          lang: config.lang,
        };

        if (path.extname(page) === ".js") {
          return {
            [replacePageExtension(page, ".js")]:
              "<!DOCTYPE html>" +
              ReactDOMServer.renderToStaticMarkup(
                <BaseContext.Provider value={baseContextValue}>
                  {context(page).default({})}
                </BaseContext.Provider>
              ),
          };
        }

        if (path.extname(page) === ".mdx") {
          return {
            [replacePageExtension(page, ".mdx")]:
              "<!DOCTYPE html>" +
              ReactDOMServer.renderToStaticMarkup(
                <BaseContext.Provider value={baseContextValue}>
                  <MDXProvider
                    components={{
                      wrapper: (props) => <BaseMDX {...props} />,
                    }}
                  >
                    {context(page).default({})}
                  </MDXProvider>
                </BaseContext.Provider>
              ),
          };
        }

        return {};
      })
    );
  } catch (e) {
    console.log("Error in compilation:");
    console.log(e);
  }

  return pages;
}

if (!isServer) {
  const context = getContext();

  const rootDom = document.getElementById("root");
  const pageId = rootDom.getAttribute("data-lighter-page-id");
  const scope = rootDom.getAttribute("data-lighter-page-scope");
  const pages = rootDom.getAttribute("data-lighter-pages");

  if (scope === "react") {
    ReactDOM.hydrate(
      <BaseContext.Provider
        value={{
          pages: JSON.parse(pages),
          styleguidePath: config.styleguidePath,
        }}
      >
        <MDXProvider
          components={{
            wrapper: (props) => <BaseMDX {...props} />,
          }}
        >
          {context(pageId).default({})}
        </MDXProvider>
      </BaseContext.Provider>,
      rootDom
    );
  }
}

export default function render(locals) {
  function getJsAssetOrderWeight(asset) {
    if (asset.match(/hotDevClient.js/)) {
      return 0;
    }

    if (asset.match(/\/polyfills\..*.*js/)) {
      return 1;
    }

    return 2;
  }

  function jsAssetsSorter(a, b) {
    return getJsAssetOrderWeight(a) - getJsAssetOrderWeight(b);
  }

  const assets = Object.keys(locals.webpackStats.compilation.assets);

  const css = assets
    .filter((value) => value.match(/\.css$/))
    .filter((value) => !value.match(/styleguide\..*.*css/));

  const js = assets
    .filter((value) => value.match(/\.js$/))
    // filter out chunks
    .filter((value) => !value.match(/\.chunk\.js/))
    // filter out styleguide stuff
    .filter((value) => !value.match(/\/styleguide\..*.*js/))
    .filter((value) => !value.match(/styleguide.js$/))
    // hotDevClient auto-reloads pages in development
    .filter(
      (value) =>
        !(
          process.env.NODE_ENV === "production" &&
          value.match(/hotDevClient.js/)
        )
    )
    .sort(jsAssetsSorter);

  const pages = getPages({ css, js });

  return Promise.resolve(pages);
}
